import {
    Alert,
    Column,
    Container,
    GenericModal,
    Grid,
    getElementValueByCode,
    Header,
    Label,
    PrimaryButton,
    Row,
    Tabbed,
    useAlignments,
    useSizes,
} from 'metrix-common3';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ProfileRisks from '../Risks';

const ProfileQuotes = (props) => {
    const intl = useIntl();
    const sizes = useSizes();
    const alignments = useAlignments();

    const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
    const [acceptPopup, setAcceptPopup] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        if (props.reference?.doGetReferences && props.reference.references && !props.reference.references.Insurer) {
            props.reference.doGetReferences('Insurer');
        }
    }, []);

    const getQuoteTabs = () => {
        const quoteTabs = [];

        if (props.policySummary?.quotes && props.policySummary.quotes.length > 0) {
            props.policySummary.quotes.forEach((quote) => {
                if (props.reference?.references?.Insurer) {
                    const insurerName = getElementValueByCode(props.reference.references.Insurer, quote.insurer);
                    const price = quote.formattedEstimatedAnnualGross || 'N/A';
                    const userChoiceText = quote.isUserChoice
                        ? ` (${intl.formatMessage({ id: 'Profile_CommonComponents_Quotes.userCurrentChoice' })})`
                        : '';

                    const coveragePercentage = quote?.coveragePercentage ? ` | ${quote.coveragePercentage}` : '';
                    const displayText =
                        (quote?.coveragePercentage === '0%' || quote?.coveragePercentage === 0) && quote?.tierName
                            ? ` | ${quote.tierName}`
                            : coveragePercentage;
                    quoteTabs.push({
                        code: insurerName + displayText,
                        description: price + userChoiceText,
                    });
                }
            });
        }

        return quoteTabs;
    };

    const quotesCount = props.policySummary.quotes && (props.policySummary.quotes.length || 0);
    const quotes = props.policySummary.quotes && props.policySummary.quotes.length > 0 && props.policySummary.quotes;
    const currentQuote = quotes?.[currentQuoteIndex];

    // Hide accept button if backend indicates it should be hidden
    // Note: 'referred' status and 'isSingleQuote' are handled in backend IsAcceptQuoteBtnHidden calculation
    const isAcceptQuoteBtnHidden = currentQuote?.isAcceptQuoteBtnHidden === true;

    const isSnCommercial = props.policySummary?.productClass === 'sn-commercial';
    const isCoverageDetailsHidden =
        props.productSettings?.coverageDetailsHiddenInSummary ||
        (props.policySummary?.status === 'quoted' && isSnCommercial) ||
        props.policySummary?.status === 'referred' ||
        props.policySummary?.status === 'accepted';
    const isClient = props.context.session.userType === 'client';

    const lgTabs = quotesCount === 1 ? 12 : quotesCount === 2 || quotesCount === 4 ? 6 : 4;

    const amounts = {
        estimatedAnnualPremium: currentQuote?.formattedEstimatedAnnualPremium || 0,
        estimatedAnnualGross: currentQuote?.formattedEstimatedAnnualGross || 0,
        estimatedMonthlyGross: currentQuote?.formattedEstimatedMonthlyGross || 0,
        referenceNumber: currentQuote?.referenceNumber || '',
    };

    const estimatedTaxes = currentQuote?.estimatedTaxes?.value || 0;

    return (
        <Container>
            {currentQuote && (
                <React.Fragment>
                    <Container bottom={sizes.sm}>
                        <Tabbed
                            items={getQuoteTabs()}
                            clickEvent={(value) => setCurrentQuoteIndex(value)}
                            descriptionSize={sizes.md}
                            lg={lgTabs}
                        />
                    </Container>
                    {props.context.session.userType === 'broker' &&
                        currentQuote.messages &&
                        currentQuote.messages.length > 0 && (
                            <Alert
                                mode={'danger'}
                                header={<FormattedMessage id='Profile_CommonComponents_Quotes.messagesHeader' />}
                                items={currentQuote.messages}
                            />
                        )}
                    <Container top={sizes.sm}>
                        <Grid>
                            <Row>
                                <Column xs={1} md={2} />
                                <Column xs={10} md={8}>
                                    <Header
                                        size={sizes.md}
                                        bottom={sizes.xs}
                                        text={
                                            (estimatedTaxes > 0 && (
                                                <FormattedMessage
                                                    id='Profile_CommonComponents_Quotes.taxesIncluded'
                                                    values={amounts}
                                                />
                                            )) ||
                                            amounts.estimatedAnnualGross
                                        }
                                    />
                                    <Header
                                        size={sizes.sm}
                                        bottom={sizes.xs}
                                        text={
                                            <FormattedMessage
                                                id='Profile_CommonComponents_Quotes.quoteNo'
                                                values={amounts}
                                            />
                                        }
                                    />
                                    {!isAcceptQuoteBtnHidden && (
                                        <Container inline horizontalAlignment={alignments.center}>
                                            <PrimaryButton
                                                text={
                                                    <FormattedMessage id='Profile_CommonComponents_Quotes.acceptQuote' />
                                                }
                                                clickEvent={() => {
                                                    if (isClient) {
                                                        setAcceptPopup(true);
                                                    } else {
                                                        props.doReduceParameter({
                                                            code: 'AcceptedQuote',
                                                            value: currentQuote.guid,
                                                        });
                                                        props.doExecutePolicyAction({ code: 'ACCEPTQUOTE' });
                                                    }
                                                }}
                                            />
                                            {acceptPopup && isClient && (
                                                <GenericModal
                                                    animate
                                                    visible
                                                    closeEvent={() => setAcceptPopup(false)}
                                                    items={
                                                        <Container>
                                                            <Header
                                                                text={
                                                                    <FormattedMessage id='Profile_CommonComponents_Quotes.acceptingQuoteTitle' />
                                                                }
                                                                size={sizes.lg}
                                                                bottom={sizes.md}
                                                            />
                                                            <Label
                                                                text={
                                                                    <FormattedMessage id='Profile_CommonComponents_Quotes.acceptingQuoteConfirmation' />
                                                                }
                                                                bold
                                                                textAlign={alignments.left}
                                                                bottom={sizes.md}
                                                            />
                                                            <Label
                                                                text={
                                                                    <FormattedMessage id='Profile_CommonComponents_Quotes.acceptingQuoteNotification' />
                                                                }
                                                                textAlign={alignments.left}
                                                                bottom={sizes.xs}
                                                            />
                                                        </Container>
                                                    }
                                                    actionButtons={[
                                                        {
                                                            text: (
                                                                <FormattedMessage id='Profile_CommonComponents_Quotes.yes' />
                                                            ),
                                                            clickEvent: () => {
                                                                props.doReduceParameter({
                                                                    code: 'AcceptedQuote',
                                                                    value: currentQuote.guid,
                                                                });
                                                                props.doExecutePolicyAction({ code: 'ACCEPTQUOTE' });
                                                                if (props.updateProgressBar) {
                                                                    props.updateProgressBar('quoteAccepted');
                                                                }
                                                                setAcceptPopup(false);
                                                            },
                                                        },
                                                        {
                                                            text: (
                                                                <FormattedMessage id='Profile_CommonComponents_Quotes.cancel' />
                                                            ),
                                                            clickEvent: () => {
                                                                setAcceptPopup(false);
                                                            },
                                                        },
                                                    ]}
                                                />
                                            )}
                                        </Container>
                                    )}
                                </Column>
                                <Column xs={1} md={2} />
                            </Row>
                        </Grid>
                    </Container>
                    {!isCoverageDetailsHidden && (
                        <ProfileRisks risks={currentQuote?.risks || []} currentQuote={currentQuote} {...props} />
                    )}
                </React.Fragment>
            )}
        </Container>
    );
};

export default ProfileQuotes;
